// Authentication Service
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  setPersistence,
  browserLocalPersistence,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver,
  EmailAuthProvider,
  reauthenticateWithCredential,
  TotpMultiFactorGenerator
} from 'firebase/auth';
import { auth } from '../firebase-config';

// ============================================================================
// Email Verification
// ============================================================================
export const sendVerificationEmail = async () => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    if (user.emailVerified) return { success: true, alreadyVerified: true };
    await sendEmailVerification(user);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message, code: error.code };
  }
};

export const isEmailVerified = () => {
  return auth.currentUser?.emailVerified || false;
};

// ============================================================================
// MFA (Multi-Factor Authentication) - SMS based
// ============================================================================

/**
 * Check if current user has MFA enrolled
 */
export const isMfaEnrolled = () => {
  const user = auth.currentUser;
  if (!user) return false;
  return multiFactor(user).enrolledFactors.length > 0;
};

/**
 * Get enrolled MFA factors
 */
export const getMfaFactors = () => {
  const user = auth.currentUser;
  if (!user) return [];
  return multiFactor(user).enrolledFactors;
};

/**
 * Initialize invisible reCAPTCHA verifier
 * Must be called before sending SMS
 */
export const initRecaptcha = (containerId = 'recaptcha-container') => {
  try {
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
    }
    window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
      callback: () => {},
      'expired-callback': () => {
        console.log('reCAPTCHA expired');
      }
    });
    return window.recaptchaVerifier;
  } catch (error) {
    console.error('reCAPTCHA init error:', error);
    return null;
  }
};

/**
 * Start MFA enrollment - sends SMS to phone number
 * Returns { verificationId }
 */
export const startMfaEnrollment = async (phoneNumber) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    
    const session = await multiFactor(user).getSession();
    
    const recaptchaVerifier = initRecaptcha();
    if (!recaptchaVerifier) throw new Error('Failed to initialize reCAPTCHA');
    
    const phoneInfoOptions = {
      phoneNumber: phoneNumber,
      session: session
    };
    
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
      phoneInfoOptions,
      recaptchaVerifier
    );
    
    return { success: true, verificationId };
  } catch (error) {
    console.error('MFA enrollment start error:', error);
    // Clean up recaptcha on error
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch(e) {}
      window.recaptchaVerifier = null;
    }
    return { success: false, error: error.message, code: error.code };
  }
};

/**
 * Complete MFA enrollment with SMS verification code
 */
export const completeMfaEnrollment = async (verificationId, verificationCode) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    
    const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
    const assertion = PhoneMultiFactorGenerator.assertion(cred);
    
    await multiFactor(user).enroll(assertion, 'Phone SMS');
    
    return { success: true };
  } catch (error) {
    console.error('MFA enrollment complete error:', error);
    return { success: false, error: error.message, code: error.code };
  }
};

/**
 * Unenroll MFA factor
 */
export const unenrollMfa = async (factorIndex = 0) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    
    const factors = multiFactor(user).enrolledFactors;
    if (factors.length === 0) throw new Error('No MFA factors enrolled');
    
    await multiFactor(user).unenroll(factors[factorIndex]);
    
    return { success: true };
  } catch (error) {
    console.error('MFA unenroll error:', error);
    return { success: false, error: error.message, code: error.code };
  }
};

/**
 * Start resolving MFA challenge during login - sends SMS
 * Called when signIn throws auth/multi-factor-auth-required
 */
export const startMfaSignIn = async (error) => {
  try {
    const resolver = getMultiFactorResolver(auth, error);
    
    const phoneHint = resolver.hints.find(
      hint => hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID
    );
    
    if (!phoneHint) throw new Error('No phone factor found. Contact support.');
    
    const recaptchaVerifier = initRecaptcha();
    if (!recaptchaVerifier) throw new Error('Failed to initialize reCAPTCHA');
    
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
      { multiFactorHint: phoneHint, session: resolver.session },
      recaptchaVerifier
    );
    
    return { 
      success: true, 
      verificationId, 
      resolver,
      phoneNumber: phoneHint.phoneNumber 
    };
  } catch (error2) {
    console.error('MFA sign-in start error:', error2);
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch(e) {}
      window.recaptchaVerifier = null;
    }
    return { success: false, error: error2.message, code: error2.code };
  }
};

/**
 * Complete MFA challenge during login with SMS code
 */
export const completeMfaSignIn = async (resolver, verificationId, verificationCode) => {
  try {
    const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
    const assertion = PhoneMultiFactorGenerator.assertion(cred);
    
    const userCredential = await resolver.resolveSignIn(assertion);
    return { success: true, user: userCredential.user };
  } catch (error) {
    console.error('MFA sign-in complete error:', error);
    return { success: false, error: error.message, code: error.code };
  }
};

// ============================================================================
// Friendly Error Messages
// ============================================================================
const friendlyAuthError = (error) => {
  const code = error.code || '';
  const map = {
    'auth/network-request-failed': 'Connection failed. Please check your internet and try again.',
    'auth/invalid-credential': 'Invalid email or password. Please try again.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/user-disabled': 'This account has been disabled. Contact support.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/weak-password': 'Password must be at least 6 characters.',
    'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled.',
    'auth/requires-recent-login': 'Please sign in again to complete this action.',
    'auth/popup-closed-by-user': 'Sign-in popup was closed. Please try again.',
    'auth/internal-error': 'Something went wrong. Please try again.',
  };
  return map[code] || error.message || 'An unexpected error occurred.';
};

export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    return { success: false, error: friendlyAuthError(error), code: error.code };
  }
};

export const registerUser = async (email, password, displayName) => {
  try {
    await setPersistence(auth, browserLocalPersistence);
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(userCredential.user, { displayName: displayName });
    return { success: true, user: userCredential.user };
  } catch (error) {
    return { success: false, error: friendlyAuthError(error), code: error.code };
  }
};

export const loginUser = async (email, password) => {
  try {
    await setPersistence(auth, browserLocalPersistence);
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error) {
    // Auto-retry once on network failure (common on mobile PWAs)
    if (error.code === 'auth/network-request-failed') {
      try {
        await new Promise(r => setTimeout(r, 1500));
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        return { success: true, user: userCredential.user };
      } catch (retryError) {
        return { success: false, error: friendlyAuthError(retryError), code: retryError.code };
      }
    }
    return { success: false, error: friendlyAuthError(error), code: error.code };
  }
};

// Session & token policy (2026-10-06): marks a deliberate local sign-out so the
// session gate does not show "Your session has ended" for it.
export const LOCAL_SIGNOUT_FLAG = 'firmassist.localSignOut';
export const logoutUser = async () => {
  try {
    try { sessionStorage.setItem(LOCAL_SIGNOUT_FLAG, '1'); } catch (e) { /* storage unavailable */ }
    await signOut(auth);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

export const getCurrentUser = () => {
  return auth.currentUser;
};

// ── Registration-in-progress coordination flag ──────────────────────────────
// Production incident (Michael Sarkodie / Amina Muniratu, 2026-09-24): App.js
// runs its OWN independent onAuthChange listener (the P0-4 "stranded user"
// recovery net) which, on seeing a signed-in user with no users/{uid}
// profile, immediately signs them out and shows a generic setup error. That
// listener fires the INSTANT createUserWithEmailAndPassword/
// signInWithEmailAndPassword resolves — which, for a brand-new registration,
// is BEFORE Login.js's own handleRegister has had a chance to call
// bootstrapNewFirm (the callable that actually CREATES that profile). Since
// App.js's path is a single Firestore read versus handleRegister's several
// sequential awaits (updateProfile, authStateReady, checkPendingInvite,
// THEN bootstrapNewFirm), App.js's sign-out reliably wins the race — this
// is a structural, 100%-reproducible ordering problem, not an intermittent
// timing edge case, and explains why no prior client-SDK-timing fix
// (getIdToken(true), authStateReady()) had any effect: neither addressed
// the actual blocker.
//
// This flag lets Login.js tell App.js's listener "a registration/bootstrap
// I own is currently in flight for this session — a missing profile right
// now is EXPECTED, not evidence of a stranded account; stand down and let
// my own flow's error handling be authoritative." It does not weaken the
// original P0-4 protection for its real target (a genuinely orphaned
// session with no registration in progress) — App.js's existing sign-out
// behavior is unchanged whenever this flag is false.
//
// Session gate (2026-10-06): the flag is now observable, so the gate re-decides
// the moment a registration finishes (a still-missing profile then follows the
// P0-4 sign-out; a created profile proceeds through the live listener).
let _registrationInProgress = false;
const _registrationListeners = new Set();
export const setRegistrationInProgress = (value) => {
  const next = !!value;
  if (next === _registrationInProgress) return;
  _registrationInProgress = next;
  _registrationListeners.forEach((cb) => { try { cb(next); } catch (_) { /* ignore */ } });
};
export const isRegistrationInProgress = () => _registrationInProgress;

// Incident 2026-10-21: after a SUCCESSFUL acceptFirmInvite / bootstrapNewFirm the
// profile exists server-side, but the live users/{uid} listener may deliver it a
// moment after the callable returns. Releasing the hold immediately could let the
// gate decide PROFILE_MISSING for a member who has just joined. Instead the hold
// is kept until the session reaches ACTIVE (App.js calls releaseRegistrationHold),
// with a bounded fallback so a hold can never outlive its purpose.
export const REGISTRATION_HOLD_MAX_MS = 20000;
let _holdTimer = null;
export const holdRegistrationUntilActive = (maxMs = REGISTRATION_HOLD_MAX_MS) => {
  if (_holdTimer) clearTimeout(_holdTimer);
  setRegistrationInProgress(true);
  _holdTimer = setTimeout(() => { _holdTimer = null; setRegistrationInProgress(false); }, maxMs);
};
export const releaseRegistrationHold = () => {
  if (!_holdTimer) return; // only a post-success hold is released here — never a flow still in progress
  clearTimeout(_holdTimer);
  _holdTimer = null;
  setRegistrationInProgress(false);
};
export const onRegistrationProgressChange = (cb) => { _registrationListeners.add(cb); return () => _registrationListeners.delete(cb); };

// ============================================================================
// Fresh re-authentication (firm deletion requires a sign-in within 5 minutes)
// ============================================================================
// Uses Firebase's own reauthentication for the email/password provider. The
// password goes ONLY to Firebase Auth (never to a FirmAssist callable). If the
// account has an authenticator-app (TOTP) second factor, the caller completes
// it with completeReauthenticationWithTotp. A phone-only second factor is not
// handled here: the caller is told to sign out and sign in again instead.
export const reauthenticateWithPassword = async (password) => {
  const user = auth.currentUser;
  if (!user || !user.email) return { success: false, error: 'You are not signed in.' };
  try {
    await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
    await user.getIdToken(true);
    return { success: true };
  } catch (error) {
    if (error && error.code === 'auth/multi-factor-auth-required') {
      const resolver = getMultiFactorResolver(auth, error);
      const totpHint = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
      if (totpHint) return { success: false, mfaRequired: true, resolver, hintUid: totpHint.uid };
      return { success: false, code: 'unsupported-second-factor', error: 'Please sign out and sign in again, then retry within 5 minutes.' };
    }
    const wrong = ['auth/wrong-password', 'auth/invalid-credential', 'auth/invalid-login-credentials'].includes(error && error.code);
    // `code` (additive, 2026-10-06) lets callers word specific failures; messages unchanged.
    return { success: false, code: (error && error.code) || 'unknown', error: wrong ? 'That password is not correct.' : 'We could not confirm your identity. Please try again.' };
  }
};

export const completeReauthenticationWithTotp = async (resolver, hintUid, code) => {
  try {
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(hintUid, code);
    await resolver.resolveSignIn(assertion);
    if (auth.currentUser) await auth.currentUser.getIdToken(true);
    return { success: true };
  } catch (error) {
    return { success: false, code: (error && error.code) || 'unknown', error: 'That code is not valid. Please try again.' };
  }
};
